import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button, Checkbox, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Radio, Select, Space, Table, Tabs, Tag, Tooltip, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import StepEditor from './components/StepEditor'
import UIRecorderModal from './components/UIRecorderModal'
import UIExcelImportModal from './components/UIExcelImportModal'
import * as uiApi from '@/api/uiAutomation'
import * as envApi from '@/api/environments'
import { useProjectStore } from '@/stores/project'
import { useThemeStore } from '@/stores/theme'
import { useAuthStore } from '@/stores/auth'
import type { UIAssertionItem, UIExecutionResult, UIStepItem, UIScript } from '@/types'
import '../../styles/aurora-page.css'
import '../../styles/aurora-modal.css'

const { Title, Text } = Typography

/** 脚本状态: 1=draft/2=active/3=paused */
const statusMap: Record<number, { text: string; color: string }> = {
  1: { text: '草稿', color: 'default' },
  2: { text: '启用', color: 'processing' },
  3: { text: '暂停', color: 'warning' },
}

/** 执行结果: 1=pending/2=running/3=passed/4=failed/5=error */
const resultMap: Record<UIExecutionResult, { text: string; color: string }> = {
  1: { text: '排队中', color: 'default' },
  2: { text: '执行中', color: 'processing' },
  3: { text: '成功', color: 'success' },
  4: { text: '失败', color: 'error' },
  5: { text: '错误', color: 'warning' },
}

const EFFORT_OPTIONS = [
  { value: 'low', label: '低（快速判定）' },
  { value: 'medium', label: '中（标准判定）' },
  { value: 'high', label: '高（深度判定）' },
]

const EMPTY_FLOW_STEPS: UIStepItem[] = []

/**
 * 由步骤组成推断用例形态（展示用，不改变存储的 case_type）：
 * 步骤全为同一类型 → 对应步骤式（AI 步骤式/元素步骤式/脚本步骤式）；
 * 多种类型混合 → 混合步骤式；无步骤 → 回退存储的 case_type
 */
function deriveCaseType(script: UIScript): { text: string; color: string } {
  const steps = script.steps?.steps || []
  if (!steps.length) {
    return script.case_type === 'script'
      ? { text: '脚本式', color: 'purple' }
      : { text: 'AI 步骤式', color: 'blue' }
  }
  const kinds = new Set(
    steps.map((s) => (s.is_script ? 'script' : s.element_id != null || s.action ? 'element' : 'ai')),
  )
  if (kinds.size === 1) {
    const kind = [...kinds][0]
    if (kind === 'script') return { text: '脚本步骤式', color: 'purple' }
    if (kind === 'element') return { text: '元素步骤式', color: 'cyan' }
    return { text: 'AI 步骤式', color: 'blue' }
  }
  return { text: '混合步骤式', color: 'orange' }
}

/** 部署形态判定：本机功能（可视化录制/接管执行）依赖平台所在机器弹出浏览器窗口供人工操作， */
/** 远程地址（IP/域名）访问时窗口在服务器上、操作者够不到，判定为部署版并提示暂不可用 */
const isServerDeployment = () => {
  const h = window.location.hostname
  return h !== 'localhost' && h !== '127.0.0.1'
}
const LOCAL_ONLY_TIP =
  '该功能需要在平台所在机器上弹出浏览器窗口供人工操作，通过远程地址访问部署版时暂不可用（本机部署/本地开发模式下可用）'

/** 空流程定义 */
const emptyFlow = () => ({
  case_type: 'ai_steps' as const,
  url: '',
  steps: [] as UIStepItem[],
  assertions: [] as UIAssertionItem[],
  bypass_cache: false,
  retry: 1,
})

export default function UIAutomation() {
  const { currentProject } = useProjectStore()
  const projectId = currentProject?.id || 0
  const navigate = useNavigate()
  const isDark = useThemeStore((s) => s.isDark)

  // 列表状态
  const [scripts, setScripts] = useState<UIScript[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [keyword, setKeyword] = useState('')
  // 列表筛选：形态（后端按步骤组成实时判定）/ 状态
  const [caseTypeFilter, setCaseTypeFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<number>(0)

  // 编辑抽屉
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<UIScript | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState('info')
  const [steps, setSteps] = useState<UIStepItem[]>(EMPTY_FLOW_STEPS)
  const [assertions, setAssertions] = useState<UIAssertionItem[]>([])
  const [form] = Form.useForm()

  // 执行环境选择弹窗
  const [runModalOpen, setRunModalOpen] = useState(false)
  const [runMode, setRunMode] = useState<'execute' | 'dry_run'>('execute')
  // 批量执行（二期：多选用例逐个入队，单条失败不阻塞其余）
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([])
  const [batchModalOpen, setBatchModalOpen] = useState(false)
  const [batchLaunching, setBatchLaunching] = useState(false)
  const [batchEnvId, setBatchEnvId] = useState<number | undefined>(undefined)
  // 执行方式：headless=后台执行（无头浏览器，仅日志）/ headed=画面执行（无头 + 截屏流，画面内嵌弹窗）/ attach=接管执行（CDP 连接已打开的调试浏览器）
  const [runExecMode, setRunExecMode] = useState<'headless' | 'headed' | 'attach'>('headless')
  const [attachStartUrl, setAttachStartUrl] = useState('')
  const [attachPort, setAttachPort] = useState(9222)
  // 调试浏览器就绪状态与打开流程反馈（打开后轮询端口，就绪后提示先登录再执行）
  const [attachLaunching, setAttachLaunching] = useState(false)
  const [attachReady, setAttachReady] = useState(false)
  const attachPollRef = useRef<number | null>(null)
  const [runScript, setRunScript] = useState<UIScript | null>(null)
  const [environments, setEnvironments] = useState<{ id: number; name: string; url: string }[]>([])
  const [envId, setEnvId] = useState<number | undefined>(undefined)
  const [launching, setLaunching] = useState(false)

  // 执行进度弹窗（实时日志 + 显性执行内嵌浏览器画面）
  const [execModalOpen, setExecModalOpen] = useState(false)
  const [executionId, setExecutionId] = useState<number | null>(null)
  const [execLog, setExecLog] = useState('')
  const [execResult, setExecResult] = useState<UIExecutionResult | null>(null)
  const [execDuration, setExecDuration] = useState(0)
  // 本次执行方式（决定弹窗左侧是否展示浏览器实时画面：headed=画面执行 / attach=接管执行）
  const [execMode, setExecMode] = useState<'headless' | 'headed' | 'attach'>('headless')
  // 最新画面帧（JPEG base64，SSE 流式刷新；执行结束自动清空收起）
  const [execFrame, setExecFrame] = useState<string | null>(null)
  const logRef = useRef<HTMLPreElement>(null)

  // 执行引擎 worker 状态
  const [workerStatus, setWorkerStatus] = useState<{ running: boolean; last_beat: number | null; queue_len: number } | null>(null)

  // ===== 四期：可视化录制 / Excel 导入 =====
  const [recorderOpen, setRecorderOpen] = useState(false)
  /** StepEditor 元素列表刷新信号（录制导入沉淀新元素后 +1） */
  const [elementRefreshKey, setElementRefreshKey] = useState(0)
  const [excelOpen, setExcelOpen] = useState(false)

  const loadWorkerStatus = useCallback(async () => {
    try {
      setWorkerStatus(await uiApi.getWorkerStatus())
    } catch {
      setWorkerStatus({ running: false, last_beat: null, queue_len: 0 })
    }
  }, [])

  // 进入用例管理页面时一次性查询 worker 状态，不轮询（可通过「刷新」按钮手动更新）
  useEffect(() => {
    loadWorkerStatus()
  }, [loadWorkerStatus])

  const fetchScripts = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const res = await uiApi.listScripts({
        project_id: projectId, page, page_size: pageSize, keyword,
        case_type: caseTypeFilter || undefined,
        status: statusFilter || undefined,
      })
      setScripts(res.data)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [projectId, page, pageSize, keyword, caseTypeFilter, statusFilter])

  useEffect(() => {
    fetchScripts()
  }, [fetchScripts])

  // 三期 D：flaky 用例识别（取趋势看板 flaky 清单，列表中标记）
  const [flakyIds, setFlakyIds] = useState<Set<number>>(new Set())
  useEffect(() => {
    if (!projectId) return
    uiApi.getTrends(projectId, 30)
      .then((data) => setFlakyIds(new Set((data.flaky || []).map((f) => f.script_id))))
      .catch(() => setFlakyIds(new Set()))
  }, [projectId])

  // ============ 执行进度轮询 ============
  useEffect(() => {
    if (!execModalOpen || !executionId) return
    let stopped = false
    const poll = async () => {
      try {
        const detail = await uiApi.getExecution(executionId)
        if (stopped) return
        setExecLog(detail.log)
        setExecResult(detail.result)
        setExecDuration(detail.duration_ms)
        if (detail.result !== 1 && detail.result !== 2) stopped = true
      } catch {
        // 轮询失败静默重试
      }
    }
    poll()
    const timer = window.setInterval(poll, 2000)
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [execModalOpen, executionId])

  // ============ 画面执行实时帧（SSE 流式接收 + 降级轮询） ============
  // 仅画面执行且未结束时订阅；执行结束（浏览器关闭）后断流清空画面，弹窗只保留日志。
  // EventSource 不支持自定义 Authorization header，故用 fetch 流式读取 SSE；
  // SSE 异常断开时回退 250ms 轮询，保证画面不中断。
  useEffect(() => {
    const running = execResult === null || execResult === 1 || execResult === 2
    if (!execModalOpen || !executionId || execMode === 'headless' || !running) return
    let stopped = false
    let controller: AbortController | null = null
    let fallbackTimer: number | null = null

    const startPolling = () => {
      if (fallbackTimer !== null || stopped) return
      fallbackTimer = window.setInterval(async () => {
        try {
          const data = await uiApi.getExecutionFrame(executionId)
          if (!stopped && data.image) setExecFrame(data.image)
        } catch {
          // 静默重试
        }
      }, 250)
    }

    const startStream = async () => {
      controller = new AbortController()
      try {
        const token = useAuthStore.getState().token
        const res = await fetch(`/api/v1/ui-automation/executions/${executionId}/frame/stream`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          signal: controller.signal,
        })
        if (!res.ok || !res.body) throw new Error(`SSE 连接失败: HTTP ${res.status}`)
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buf = ''
        for (;;) {
          const { done, value } = await reader.read()
          if (stopped) return
          if (done) break
          buf += decoder.decode(value, { stream: true })
          // 按 SSE 事件分隔符切帧（data: <base64>\n\n）
          let idx: number
          while ((idx = buf.indexOf('\n\n')) >= 0) {
            const chunk = buf.slice(0, idx)
            buf = buf.slice(idx + 2)
            const dataLine = chunk.split('\n').find((l) => l.startsWith('data: '))
            if (dataLine) {
              const image = dataLine.slice(6).trim()
              if (image) setExecFrame(image)
            }
          }
        }
      } catch (err) {
        if (stopped || (err instanceof DOMException && err.name === 'AbortError')) return
      }
      if (!stopped) startPolling() // 流结束/异常 → 降级轮询
    }

    startStream()
    return () => {
      stopped = true
      controller?.abort()
      if (fallbackTimer !== null) window.clearInterval(fallbackTimer)
    }
  }, [execModalOpen, executionId, execMode, execResult])

  // 日志自动滚动到底部
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [execLog])

  // ============ 编辑 ============
  const handleCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ platform: 'web', status: 1, bypass_cache: false, is_public: false, module_name: '' })
    setSteps([])
    setAssertions([])
    setActiveTab('info')
    // Drawer 关闭不销毁 StepEditor，打开时刷新元素/公共模块列表（期间可能有新增/变更）
    setElementRefreshKey((k) => k + 1)
    setDrawerOpen(true)
  }

  const handleEdit = (record: UIScript) => {
    setEditing(record)
    form.resetFields()
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      platform: record.platform,
      url: record.url,
      status: record.status,
      cache_version: record.cache_version,
      bypass_cache: record.bypass_cache,
      is_public: record.is_public || false,
      module_name: record.module_name || '',
    })
    setSteps(record.steps?.steps || [])
    setAssertions(record.steps?.assertions || [])
    setActiveTab('info')
    setElementRefreshKey((k) => k + 1)
    setDrawerOpen(true)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (steps.length === 0) {
        message.warning('请至少添加一个执行步骤')
        setActiveTab('steps')
        return
      }
      // 元素步骤 = 非脚本且带 action/element_id 标记
      const isElementStep = (s: UIStepItem) => !s.is_script && (s.element_id != null || !!s.action)
      if (steps.some((s) => !s.is_script && !isElementStep(s) && !s.description.trim())) {
        message.warning('AI 步骤的描述不能为空')
        setActiveTab('steps')
        return
      }
      if (steps.some((s) => isElementStep(s) && s.element_id == null)) {
        message.warning('元素步骤请选择元素库元素')
        setActiveTab('steps')
        return
      }
      if (steps.some((s) => isElementStep(s) && s.action === 'input' && !(s.data?.value || '').trim())) {
        message.warning('元素步骤的输入内容不能为空')
        setActiveTab('steps')
        return
      }
      if (steps.some((s) => s.is_script && !s.script.trim())) {
        message.warning('脚本步骤的脚本内容不能为空')
        setActiveTab('steps')
        return
      }
      // 四期 H：公共模块步骤必须选择模块
      if (steps.some((s) => s.module_id != null && !s.module_id)) {
        message.warning('公共模块步骤请选择要引用的公共模块')
        setActiveTab('steps')
        return
      }
      setSubmitting(true)
      const payload = {
        project_id: projectId,
        name: values.name,
        description: values.description || '',
        platform: values.platform || 'web',
        url: values.url || '',
        status: values.status,
        cache_version: values.cache_version || '',
        bypass_cache: values.bypass_cache || false,
        is_public: values.is_public || false,
        module_name: values.module_name || '',
        steps: {
          case_type: 'ai_steps' as const,
          url: values.url || '',
          steps,
          assertions,
          bypass_cache: values.bypass_cache || false,
          retry: 1,
        },
      }
      if (editing) {
        await uiApi.updateScript(editing.id, payload)
        message.success('更新成功')
      } else {
        await uiApi.createScript(payload)
        message.success('创建成功')
      }
      setDrawerOpen(false)
      fetchScripts()
    } catch (err) {
      console.error('[UICases] 保存失败:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await uiApi.deleteScript(id, projectId)
      message.success('删除成功')
      fetchScripts()
    } catch (err) {
      console.error('[UICases] 删除失败:', err)
    }
  }

  // ============ 四期 I/J：录制器 / Excel 导入的草稿追加 ============
  const handleImportDraftSteps = (drafts: UIStepItem[], titleSuggestion?: string) => {
    setSteps((prev) => [...prev, ...drafts])
    // 录制导入可能沉淀了新元素，刷新 StepEditor 的元素列表，保证元素步骤下拉能显示元素名而非 ID
    setElementRefreshKey((k) => k + 1)
    // 新用例且名称为空时，采用导入识别到的用例标题作为名称建议
    if (titleSuggestion && !editing && !form.getFieldValue('name')) {
      form.setFieldsValue({ name: titleSuggestion })
    }
    setActiveTab('steps')
  }

  // ============ 执行 / 试跑 ============
  /** 执行引擎前置检查：未在线时弹窗提示（可跳转服务管理页，或无视提示继续执行）；返回是否继续执行原动作 */
  const ensureEngineRunning = async (): Promise<boolean> => {
    try {
      const res = await uiApi.getWorkerStatus()
      if (res.running) return true
    } catch (err) {
      console.error('[UICases] 查询执行引擎状态失败:', err)
      return true
    }
    return await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: '执行引擎未启动',
        content: '未检测到运行中的执行引擎（Node Worker），任务入队后将一直停留在队列中无人执行。可前往「系统配置 → 服务管理」启动执行引擎，或无视提示继续执行。',
        okText: '去启动',
        okButtonProps: { className: 'ant-btn-engine-run' },
        cancelText: '知道了，继续执行',
        cancelButtonProps: { className: 'ant-btn-engine-continue' },
        onOk: () => {
          navigate('/settings/services')
          resolve(false)
        },
        onCancel: () => resolve(true),
      })
    })
  }

  const openRunModal = async (record: UIScript, mode: 'execute' | 'dry_run') => {
    setRunScript(record)
    setRunMode(mode)
    setEnvId(undefined)
    setRunExecMode('headless')
    setAttachStartUrl('')
    setAttachPort(9222)
    setAttachLaunching(false)
    setAttachReady(false)
    if (attachPollRef.current) {
      window.clearInterval(attachPollRef.current)
      attachPollRef.current = null
    }
    setRunModalOpen(true)
    try {
      const res = await envApi.listEnvironments({ project_id: projectId, page: 1, page_size: 100 })
      setEnvironments(res.data.map((e) => ({ id: e.id, name: e.name, url: e.url })))
    } catch {
      setEnvironments([])
    }
  }

  /** 一键打开调试浏览器：入队启动指令 → 轮询调试端口（最长 20s），就绪后提示先登录 */
  const handleOpenAttachBrowser = async () => {
    if (attachLaunching) return
    // 双重保险：远程访问部署版下模式选择已被拦截，此处兜底
    if (isServerDeployment()) {
      message.warning(LOCAL_ONLY_TIP)
      return
    }
    if (!(await ensureEngineRunning())) return
    setAttachLaunching(true)
    try {
      await uiApi.openAttachBrowser(attachPort)
      message.info('调试浏览器启动指令已发送，等待窗口弹出…')
      if (attachPollRef.current) window.clearInterval(attachPollRef.current)
      let tries = 0
      attachPollRef.current = window.setInterval(async () => {
        tries += 1
        try {
          const res = await uiApi.checkAttachBrowser(attachPort)
          if (res.ready) {
            if (attachPollRef.current) window.clearInterval(attachPollRef.current)
            attachPollRef.current = null
            setAttachLaunching(false)
            setAttachReady(true)
            message.success('调试浏览器已就绪，请在窗口内完成登录后再点击「开始执行」')
            return
          }
        } catch {
          /* 单次探测失败忽略，继续轮询 */
        }
        if (tries >= 20) {
          if (attachPollRef.current) window.clearInterval(attachPollRef.current)
          attachPollRef.current = null
          setAttachLaunching(false)
          message.warning('20 秒内未检测到调试端口，请确认浏览器窗口已弹出（本机需已安装所选驱动对应的浏览器）')
        }
      }, 1000)
    } catch (err) {
      console.error('[UICases] 打开调试浏览器失败:', err)
      setAttachLaunching(false)
    }
  }

  // 接管模式下探测一次端口就绪状态：兼容用户手动以 --remote-debugging-port 启动浏览器的场景；端口变更时终止旧轮询
  useEffect(() => {
    if (!runModalOpen || runExecMode !== 'attach') return
    if (attachPollRef.current) {
      window.clearInterval(attachPollRef.current)
      attachPollRef.current = null
      setAttachLaunching(false)
    }
    let cancelled = false
    uiApi.checkAttachBrowser(attachPort)
      .then((res) => { if (!cancelled) setAttachReady(res.ready) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [runModalOpen, runExecMode, attachPort])

  // 组件卸载时清理端口轮询定时器
  useEffect(() => () => {
    if (attachPollRef.current) window.clearInterval(attachPollRef.current)
  }, [])

  const handleLaunch = async () => {
    if (!runScript) return
    if (runExecMode === 'attach' && !attachReady) {
      message.warning('调试端口未就绪：请先点击「打开调试浏览器」并在窗口内完成登录，再开始执行')
      return
    }
    if (!(await ensureEngineRunning())) return
    setLaunching(true)
    try {
      const launcher = runMode === 'dry_run' ? uiApi.dryRunScript : uiApi.executeScript
      const res = await launcher(runScript.id, projectId, {
        environment_id: envId,
        bypass_cache: runMode === 'dry_run',
        headed: runExecMode === 'headed',
        attach_browser: runExecMode === 'attach',
        attach_start_url: attachStartUrl,
        attach_cdp_port: attachPort,
      })
      setRunModalOpen(false)
      setExecutionId(res.execution_id)
      setExecLog('')
      setExecResult(2)
      setExecDuration(0)
      setExecMode(runExecMode)
      setExecFrame(null)
      setExecModalOpen(true)
      fetchScripts()
    } catch (err) {
      console.error('[UICases] 触发执行失败:', err)
    } finally {
      setLaunching(false)
    }
  }

  // 画面执行展示：仅画面执行且执行中显示，执行结束（无头浏览器关闭）后自动收起，只保留日志
  const execRunning = execResult === null || execResult === 1 || execResult === 2
  const showFramePanel = execMode !== 'headless' && execRunning

  // ============ 批量执行（二期） ============
  const handleBatchLaunch = async () => {
    if (!selectedRowKeys.length) return
    if (!(await ensureEngineRunning())) return
    setBatchLaunching(true)
    try {
      const res = await uiApi.batchExecuteScripts({
        script_ids: selectedRowKeys.map(Number),
        environment_id: batchEnvId,
      })
      const failMsg = res.failed.length
        ? `，${res.failed.length} 个失败：${res.failed.map((f) => `#${f.script_id} ${f.reason}`).join('；')}`
        : ''
      message.success(`${res.execution_ids.length} 个任务已加入执行队列${failMsg}`)
      setBatchModalOpen(false)
      setSelectedRowKeys([])
      fetchScripts()
    } catch (err) {
      console.error('[UICases] 批量执行失败:', err)
    } finally {
      setBatchLaunching(false)
    }
  }

  const columns: ColumnsType<UIScript> = [
    {
      title: '用例名称',
      dataIndex: 'name',
      width: 240,
      render: (v: string, r: UIScript) => (
        <div>
          <div style={{ fontWeight: 600, color: 'var(--au-slate)' }}>
            {v}
            {r.is_public ? (
              <Tooltip title={`公共模块：${r.module_name || r.name}（供其他用例「公共模块」步骤引用）`}>
                <Tag color="geekblue" style={{ marginLeft: 6, marginRight: 0 }}>公共</Tag>
              </Tooltip>
            ) : null}
            {flakyIds.has(r.id) ? (
              <Tooltip title="flaky 用例：近期执行成功/失败翻转或自愈频繁，详见趋势看板">
                <Tag color="red" style={{ marginLeft: 6, marginRight: 0 }}>flaky</Tag>
              </Tooltip>
            ) : null}
          </div>
          {r.description ? (
            <div style={{ fontSize: 12, color: 'var(--au-slate-muted)', marginTop: 2 }}>{r.description}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: '形态',
      key: 'case_type',
      width: 110,
      render: (_, r: UIScript) => {
        const t = deriveCaseType(r)
        return <Tag color={t.color}>{t.text}</Tag>
      },
    },
    {
      title: '步骤 / 断言',
      key: 'count',
      width: 110,
      render: (_, r: UIScript) => (
        <Text style={{ fontSize: 13 }}>
          {r.steps?.steps?.length || 0} / {r.steps?.assertions?.length || 0}
        </Text>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (v: number) => {
        const s = statusMap[v] || statusMap[1]
        return <Tag color={s.color}>{s.text}</Tag>
      },
    },
    {
      title: '最近执行',
      dataIndex: 'last_run_time',
      width: 170,
      render: (v: string | null) => (
        <span style={{ fontSize: 13, color: 'var(--au-slate-muted)'}}>
          {v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '未执行'}
        </span>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 240,
      render: (_, r: UIScript) => (
        <Space size={2}>
          <Button type="link" size="small" className="run-btn" onClick={() => openRunModal(r, 'execute')}>执行</Button>
          <Button type="link" size="small" onClick={() => openRunModal(r, 'dry_run')}>试跑</Button>
          <Button type="link" size="small" onClick={() => handleEdit(r)}>编辑</Button>
          <Popconfirm
            title="确定删除此用例？"
            onConfirm={() => handleDelete(r.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="aurora-page aurora-page--fit">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Title level={4} style={{ margin: 0, fontWeight: 600 }}>用例管理</Title>
          <Tag color="orange">Playwright 引擎</Tag>
          <Tag color="blue">AI 自然语言步骤</Tag>
        </div>
        <Space>
          <Button onClick={() => { fetchScripts(); loadWorkerStatus() }}>刷新</Button>
          <Button type="primary" onClick={handleCreate}>新建UI用例</Button>
        </Space>
      </div>

      {/* 执行引擎状态条 */}
      <div style={{
        background: 'var(--au-header-bg)',
        borderRadius: 10,
        border: `1px solid ${isDark ? 'var(--border)' : 'var(--au-border)'}`,
        padding: '10px 16px',
        marginBottom: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}>
        <Space size={12} wrap>
          <Tag color={workerStatus?.running ? 'success' : 'error'} style={{ margin: 0 }}>
            {workerStatus === null ? '检测中' : workerStatus.running ? '运行中' : '未启动'}
          </Tag>
          <Text style={{ fontSize: 13, color: 'var(--au-slate)' }}>执行引擎（Node Worker）：</Text>
          {workerStatus?.running ? (
            <Text code style={{ fontSize: 12 }}>
              在线 | 最近活动 {workerStatus.last_beat ? new Date(workerStatus.last_beat).toLocaleTimeString('zh-CN', { hour12: false }) : '-'} | 队列待执行 {workerStatus.queue_len} 个
            </Text>
          ) : (
            <Text code style={{ fontSize: 12 }}>离线 | 任务将停留在队列中无人执行</Text>
          )}
        </Space>
        {!workerStatus?.running && (
          <Text style={{ fontSize: 12, color: 'var(--au-text-faint)' }}>
            请在 engine 目录启动执行引擎：$env:REDIS_URL='redis://:密码@localhost:6379/0'; node worker.js
          </Text>
        )}
      </div>

      <div className="search-bar" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Select
          placeholder="形态"
          allowClear
          style={{ width: 130 }}
          value={caseTypeFilter || undefined}
          onChange={(v) => {
            setCaseTypeFilter(v || '')
            setPage(1)
          }}
          options={[
            { value: 'ai_steps', label: 'AI 步骤式' },
            { value: 'element_steps', label: '元素步骤式' },
            { value: 'script_steps', label: '脚本步骤式' },
            { value: 'mixed', label: '混合步骤式' },
          ]}
        />
        <Select
          placeholder="状态"
          allowClear
          style={{ width: 110 }}
          value={statusFilter || undefined}
          onChange={(v) => {
            setStatusFilter(v || 0)
            setPage(1)
          }}
          options={[
            { value: 1, label: '草稿' },
            { value: 2, label: '启用' },
            { value: 3, label: '暂停' },
          ]}
        />
        <Input
          allowClear
          placeholder="搜索用例名称"
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value)
            setPage(1)
          }}
          onPressEnter={() => setPage(1)}
          style={{ width: 260 }}
        />
        <Button
          type="primary"
          onClick={() => setPage(1)}
          style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)', color: '#fff' }}
        >
          查询
        </Button>
        <div style={{ flex: 1 }} />
        <Button onClick={() => setBatchModalOpen(true)} disabled={!selectedRowKeys.length}>
          批量执行（{selectedRowKeys.length}）
        </Button>
      </div>

      <div className="content-card">
        <Table
          columns={columns}
          dataSource={scripts}
          rowKey="id"
          loading={loading}
          scroll={{ y: '100%' }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys.map(Number)),
            selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
          }}
          pagination={{
            current: page,
            total,
            pageSize,
            showSizeChanger: true,
            showTotal: (t: number) => `共 ${t} 条`,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
        />
      </div>

      {/* ============ 编辑抽屉 ============ */}
      <Drawer
        title={editing ? '编辑 UI 用例' : '新建 UI 用例'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={720}
        rootClassName="aurora-edit-drawer"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
            <Button type="primary" loading={submitting} onClick={handleSubmit}>保存</Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            tabBarStyle={{ paddingLeft: 24 }}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
            items={[
              {
                key: 'info',
                label: '基本信息',
                children: (
                  <div style={{ padding: '0 24px 16px', maxWidth: 640 }}>
                    <Form.Item name="name" label="用例名称" rules={[{ required: true, message: '请输入用例名称' }]}>
                      <Input placeholder="如：商品搜索全流程验证" maxLength={255} />
                    </Form.Item>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <Form.Item name="platform" label="平台" style={{ flex: 1 }}>
                        <Select
                          options={[
                            { value: 'web', label: 'Web' },
                            { value: 'android', label: 'Android' },
                            { value: 'ios', label: 'iOS' },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item name="status" label="状态" style={{ flex: 1 }}>
                        <Select
                          options={[
                            { value: 1, label: '草稿' },
                            { value: 2, label: '启用' },
                            { value: 3, label: '暂停' },
                          ]}
                        />
                      </Form.Item>
                    </div>
                    <Form.Item name="url" label="默认测试地址" extra="执行时未选择环境则使用该地址；也可填相对路径，结合环境 URL 拼接">
                      <Input placeholder="https://shop.example.com 或 /login" />
                    </Form.Item>
                    <Form.Item name="description" label="描述">
                      <Input.TextArea rows={3} placeholder="用例用途说明（选填）" maxLength={2000} />
                    </Form.Item>
                    <Form.Item name="cache_version" label="缓存版本号" extra="变更后旧 AI 步骤缓存全部失效（用于页面改版后强制重新翻译）">
                      <Input placeholder="如 v1.0.0（选填）" maxLength={32} />
                    </Form.Item>
                    {/* 四期 H：公共步骤库模块（供其他用例「公共模块」步骤引用） */}
                    <Form.Item name="is_public" label="公共步骤库模块" valuePropName="checked" style={{ marginBottom: 8 }}>
                      <Checkbox>开放为公共模块，供其他用例以「公共模块」步骤引用（执行时原位展开）</Checkbox>
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(p, c) => p.is_public !== c.is_public}>
                      {({ getFieldValue }) =>
                        getFieldValue('is_public') ? (
                          <Form.Item name="module_name" label="模块名称" extra="其他用例引用时的展示名（留空则使用用例名称）">
                            <Input placeholder="如：登录态准备" maxLength={100} />
                          </Form.Item>
                        ) : null
                      }
                    </Form.Item>
                  </div>
                ),
              },
              {
                key: 'steps',
                label: `步骤（${steps.length}）`,
                children: (
                  <div style={{ padding: '0 24px 16px' }}>
                    <div style={{ fontSize: 12, color: 'var(--au-slate-muted)', marginBottom: 8, lineHeight: '20px' }}>
                      AI 步骤填写自然语言描述，执行时由 AI 结合页面实际元素翻译为操作（结果缓存，二次执行不再调用 AI）；
                      脚本步骤直接执行 Playwright 脚本，适合登录态准备等固定操作。
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <Button
                        size="small"
                        onClick={() => {
                          if (isServerDeployment()) {
                            message.warning(LOCAL_ONLY_TIP)
                            return
                          }
                          setRecorderOpen(true)
                        }}
                      >
                        可视化录制
                      </Button>
                      <Button size="small" onClick={() => setExcelOpen(true)}>导入 Excel</Button>
                      <span style={{ fontSize: 12, color: 'var(--au-slate-muted)', alignSelf: 'center' }}>
                        录制 / Excel 导入会生成步骤草稿，追加到下方步骤列表
                      </span>
                    </div>
                    <StepEditor steps={steps} onChange={setSteps} refreshKey={elementRefreshKey} />
                  </div>
                ),
              },
              {
                key: 'assertions',
                label: `断言（${assertions.length}）`,
                children: (
                  <div style={{ padding: '0 24px 16px' }}>
                    <div style={{ fontSize: 12, color: 'var(--au-slate-muted)', marginBottom: 8, lineHeight: '20px' }}>
                      断言在全部步骤执行完成后由 AI 判定：结合页面元素与正文判断是否满足预期，输出通过与置信度。
                      判定力度越高消耗 token 越多。
                    </div>
                    {assertions.map((item, index) => (
                      <div
                        key={index}
                        style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}
                      >
                        <span
                          style={{
                            width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                            background: 'var(--au-primary-100)', color: 'var(--au-primary)',
                            fontSize: 12, fontWeight: 600,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          {index + 1}
                        </span>
                        <Input
                          placeholder="断言描述，如：购物车角标显示 1"
                          value={item.assertion}
                          onChange={(e) =>
                            setAssertions(assertions.map((a, i) => (i === index ? { ...a, assertion: e.target.value } : a)))
                          }
                          style={{ flex: 1 }}
                        />
                        <Select
                          value={item.effort || 'low'}
                          options={EFFORT_OPTIONS}
                          onChange={(v) =>
                            setAssertions(assertions.map((a, i) => (i === index ? { ...a, effort: v } : a)))
                          }
                          style={{ width: 160, flexShrink: 0 }}
                        />
                        {/* 四期 C：视频断言（模块设置开启后录制视频+过程帧多模态判定，模型不支持时降级截图并标 degraded） */}
                        <Checkbox
                          checked={item.video}
                          onChange={(e) =>
                            setAssertions(assertions.map((a, i) => (i === index ? { ...a, video: e.target.checked } : a)))
                          }
                        >
                          视频
                        </Checkbox>
                        <Button
                          type="text"
                          size="small"
                          danger
                          onClick={() => setAssertions(assertions.filter((_, i) => i !== index))}
                        >
                          删除
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="dashed"
                      block
                      style={{ height: 38 }}
                      onClick={() => setAssertions([...assertions, { assertion: '', effort: 'low', video: false }])}
                    >
                      添加断言
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        </Form>
      </Drawer>

      {/* ============ 执行环境选择弹窗 ============ */}
      <Modal
        title={runMode === 'dry_run' ? `试跑「${runScript?.name || ''}」` : `执行「${runScript?.name || ''}」`}
        open={runModalOpen}
        onCancel={() => setRunModalOpen(false)}
        onOk={handleLaunch}
        confirmLoading={launching}
        okText={runMode === 'dry_run' ? '开始试跑' : '开始执行'}
        cancelText="取消"
        width={480}
        rootClassName="aurora-modal-wrap"
        className="aurora-modal"
      >
        <Form layout="vertical" style={{ marginTop: 4 }}>
          <Form.Item
            label="执行环境"
            extra={runMode === 'dry_run' ? '试跑将绕过 AI 步骤缓存执行并重新预热缓存' : 'AI 步骤命中缓存时不再调用 AI 翻译'}
          >
            <Select
              allowClear
              placeholder="选择环境（留空使用默认测试地址）"
              value={envId}
              onChange={setEnvId}
              options={environments.map((e) => ({ value: e.id, label: `${e.name}（${e.url}）` }))}
            />
          </Form.Item>
          <Form.Item
            label="执行方式"
            extra={
              runExecMode === 'attach'
                ? '接管你手动登录好的调试模式浏览器：先打开调试浏览器并完成登录，再开始执行，用例将在已登录页签上继续跑剩余步骤，画面实时内嵌左侧'
                : runExecMode === 'headed'
                  ? '不弹出浏览器窗口：实时画面内嵌至执行进度弹窗左侧，边看边执行'
                  : '浏览器在后台运行，通过执行日志观察进度'
            }
          >
            <Radio.Group
              value={runExecMode}
              onChange={(e) => {
                const v = e.target.value
                // 接管执行依赖平台所在机器弹出调试浏览器，远程访问部署版不可用（画面执行不受影响）
                if (v === 'attach' && isServerDeployment()) {
                  message.warning(
                    '接管执行需在平台所在机器弹出调试浏览器并人工登录，远程访问部署版时暂不可用（本机部署/本地开发模式下可用）；观看实时执行画面请改用「画面执行」',
                  )
                  return
                }
                setRunExecMode(v)
              }}
              options={[
                { value: 'headless', label: '后台执行' },
                { value: 'headed', label: '画面执行' },
                { value: 'attach', label: '接管执行' },
              ]}
              optionType="button"
              buttonStyle="solid"
            />
          </Form.Item>
          {runExecMode === 'attach' ? (
            <>
              <Form.Item
                label="起始 URL"
                extra="优先在该 URL 对应的页签上执行；无匹配页签时自动新开页签（与现有页签共享登录态）"
                style={{ marginBottom: 0 }}
              >
                <Input
                  value={attachStartUrl}
                  onChange={(e) => setAttachStartUrl(e.target.value)}
                  placeholder="留空默认使用用例的测试页面地址"
                  allowClear
                />
              </Form.Item>
              <Form.Item label="调试端口" extra="调试浏览器将带 --remote-debugging-port=<端口> 参数启动" style={{ marginTop: 12, marginBottom: 0 }}>
                <InputNumber
                  min={1024}
                  max={65535}
                  value={attachPort}
                  onChange={(v) => setAttachPort(Number(v) || 9222)}
                  style={{ width: 160 }}
                />
              </Form.Item>
              <Form.Item
                label="调试浏览器"
                extra="点击打开独立调试浏览器（不影响日常浏览器）→ 在窗口内输入账号密码完成登录 → 回到这里点击「开始执行」，用例将接管该窗口继续执行"
                style={{ marginTop: 12, marginBottom: 0 }}
              >
                <Space align="center">
                  <Button onClick={handleOpenAttachBrowser} loading={attachLaunching}>
                    打开调试浏览器
                  </Button>
                  {attachReady ? <Tag color="success">端口已就绪，请先完成登录</Tag> : <Tag>端口未就绪</Tag>}
                </Space>
              </Form.Item>
            </>
          ) : null}
        </Form>
      </Modal>

      {/* ============ 批量执行弹窗（二期） ============ */}
      <Modal
        title={`批量执行（已选 ${selectedRowKeys.length} 个用例）`}
        open={batchModalOpen}
        onCancel={() => setBatchModalOpen(false)}
        onOk={handleBatchLaunch}
        confirmLoading={batchLaunching}
        okText="加入执行队列"
        cancelText="取消"
        width={480}
        rootClassName="aurora-modal-wrap"
        className="aurora-modal"
      >
        <Form layout="vertical" style={{ marginTop: 4 }}>
          <Form.Item
            label="执行环境"
            extra="统一应用到所选用例；留空则各用例使用自己的默认测试地址。批量执行统一为后台模式（接管执行仅支持单用例）"
          >
            <Select
              allowClear
              placeholder="选择环境（留空使用默认测试地址）"
              value={batchEnvId}
              onChange={setBatchEnvId}
              options={environments.map((e) => ({ value: e.id, label: `${e.name}（${e.url}）` }))}
            />
          </Form.Item>
          <Form.Item label="执行顺序" extra="用例按选择顺序逐个入队，由执行引擎按队列顺序执行；单条入队失败不影响其余用例">
            <Text style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>
              {selectedRowKeys.length ? `已选：${scripts.filter((s) => selectedRowKeys.includes(s.id)).map((s) => s.name).join('、')}` : '未选择用例'}
            </Text>
          </Form.Item>
        </Form>
      </Modal>

      {/* ============ 执行进度弹窗（左：显性执行浏览器画面 / 右：实时日志） ============ */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>执行进度</span>
            {execResult !== null ? (
              <Tag color={resultMap[execResult].color}>{resultMap[execResult].text}</Tag>
            ) : null}
            {execDuration > 0 ? (
              <span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>{(execDuration / 1000).toFixed(1)}s</span>
            ) : null}
          </div>
        }
        open={execModalOpen}
        onCancel={() => setExecModalOpen(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>
              {execRunning
                ? execMode === 'attach'
                  ? '执行中：左侧为被接管页签的实时画面，执行结束后自动收起'
                  : '执行中：左侧为浏览器实时画面，执行结束后自动收起'
                : '执行已结束，可关闭窗口'}
            </Text>
            <Button type="primary" onClick={() => setExecModalOpen(false)}>关闭</Button>
          </div>
        }
        width={showFramePanel ? 'min(1500px, 96vw)' : 720}
        rootClassName="aurora-modal-wrap"
        className="aurora-modal"
      >
        <div style={{ display: 'flex', gap: 14 }}>
          {showFramePanel ? (
            <div style={{ flex: '1.6 1 0', minWidth: 0 }}>
              <div style={{ fontSize: 12, color: 'var(--au-slate-muted)', marginBottom: 6 }}>
                {execMode === 'attach' ? '被接管页签实时画面（遮挡时自动降级为低频画面）' : '浏览器实时画面（约 0.2~0.5s 延迟）'}
              </div>
              <div
                style={{
                  height: 'min(68vh, 660px)',
                  borderRadius: 10,
                  background: '#10192b',
                  border: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                {execFrame ? (
                  <img
                    src={`data:image/jpeg;base64,${execFrame}`}
                    alt="浏览器执行画面"
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                  />
                ) : (
                  <span style={{ color: '#c8d6e5', fontSize: 12 }}>等待浏览器画面...</span>
                )}
              </div>
            </div>
          ) : null}
          <div style={{ flex: 1, minWidth: 0 }}>
            {showFramePanel ? (
              <div style={{ fontSize: 12, color: 'var(--au-slate-muted)', marginBottom: 6 }}>执行日志</div>
            ) : null}
            <pre
              ref={logRef}
              style={{
                margin: 0,
                height: showFramePanel ? 'min(68vh, 660px)' : 380,
                overflow: 'auto',
                borderRadius: 10,
                padding: '12px 14px',
                fontSize: 12,
                lineHeight: '20px',
                fontFamily: 'Consolas, Menlo, monospace',
                background: '#10192b',
                color: '#c8d6e5',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {execLog || '等待执行引擎返回日志...'}
            </pre>
          </div>
        </div>
        {execResult !== null && execResult !== 1 && execResult !== 2 ? (
          <div style={{ marginTop: 12, fontSize: 12, color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)' }}>
            完整报告与失败截图可在「测试报告」与「执行中心」查看。
          </div>
        ) : null}
      </Modal>

      {/* ============ 四期 I：可视化录制 ============ */}
      <UIRecorderModal
        open={recorderOpen}
        onClose={() => setRecorderOpen(false)}
        onImport={handleImportDraftSteps}
        defaultUrl={form.getFieldValue('url') || ''}
      />

      {/* ============ 四期 J：Excel 导入 ============ */}
      <UIExcelImportModal
        open={excelOpen}
        onClose={() => setExcelOpen(false)}
        onImport={handleImportDraftSteps}
      />
    </div>
  )
}
